import { ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { array, number, object, string } from 'yup';

import { CapabilityCheck } from '@hcs/authz';
import {
  ActionButtons,
  Dialog,
  DIALOG_ACTIONS_PORTAL_ID,
  DropdownOptionType,
  Flex,
} from '@hcs/design-system';
import {
  FormFieldDialogLayoutProps,
  FormFieldsDialogDate,
  FormFieldsDialogDropdown,
  FormFieldsDialogInput,
  FormFieldsDialogMultiSelect,
} from '@hcs/forms';
import { useActiveState } from '@hcs/hooks';
import { useSelfServeAgentPlans, useSelfServePlans } from '@hcs/self-service';
import {
  CouponCreateRequest,
  CouponRedemptionType,
  SelfServePlan,
} from '@hcs/types';

import { useCouponCreate } from '../../hooks';
import { useCouponUpdate } from '../../hooks/useCouponUpdate';
import { formatRedemptionType, getPlanLabel } from '../../utils';

const NAME_ORDER = ['Basic', 'Pro', 'Teams'];
const LAYOUT_PROPS: FormFieldDialogLayoutProps = { labelWidth: 200 };
const LAYOUT_PROPS_DATETIME: FormFieldDialogLayoutProps = {
  ...LAYOUT_PROPS,
  labelHelper: {
    dataHcName: 'datetime-helper',
    helperContent: `Datetimes will be saved and applied in UTC timezone. Your current offset from UTC is ${new Date().getTimezoneOffset() / 60} hours.`,
  },
};
const REDEMPTION_TYPE_DROPDOWN_OPTIONS: DropdownOptionType<CouponRedemptionType>[] =
  [
    {
      label: formatRedemptionType('client-credit'),
      value: 'client-credit',
    },
    {
      label: formatRedemptionType('signup'),
      value: 'signup',
    },
    {
      label: formatRedemptionType('loyalty'),
      value: 'loyalty',
    },
    {
      label: formatRedemptionType('resubscribe'),
      value: 'resubscribe',
    },
    {
      label: formatRedemptionType('churn-prevention'),
      value: 'churn-prevention',
    },
  ];
function planSort(a: SelfServePlan, b: SelfServePlan): number {
  // 1. "Year" commit_period first
  if (a.commit_period === 'Year' && b.commit_period !== 'Year') return -1;
  if (a.commit_period !== 'Year' && b.commit_period === 'Year') return 1;

  // 2. If same commit_period, sort by name order
  if (a.commit_period === b.commit_period) {
    const aIdx = NAME_ORDER.indexOf(a.name);
    const bIdx = NAME_ORDER.indexOf(b.name);
    // If both names are in the order list
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    // If only one is in the order list, it comes first
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    // Otherwise, fallback to alphabetical
    return a.name.localeCompare(b.name);
  }

  // 3. Otherwise, keep original order
  return 0;
}
const YUP_SHEMA = object({
  code: string().required('A unique coupon code is required'),
  amount_off: number()
    .required('Amount off is required')
    .test(
      'is-positive',
      'Amount off must be a positive number',
      (value: number | undefined) => {
        return value != null && value > 0;
      },
    ),
  name: string().required('A name is required'),
  redemption_type: string().required(),
  redemption_cap: number().optional(),
  active_on: string().optional().nullable(),
  active_until: string()
    .optional()
    .nullable()
    .when('active_on', ([active_on]: (string | undefined)[], schema) => {
      if (active_on == null) {
        return schema;
      }
      // if returns true, no error is shown to user:
      return schema.test(
        'from-to-dates-compatible',
        '"Active Until" must be after the "Active On"',
        (active_until: string | null | undefined) => {
          if (active_until == null) {
            return true;
          }
          if (active_on && active_until) {
            return new Date(active_on) <= new Date(active_until);
          }
          return true;
        },
      );
    }),
  plan_ids: array().of(string()).optional().nullable(),
});

type FormDataType = CouponCreateRequest & {
  amount_off_dollars: number;
};

interface CouponCreateFormProps {
  actionPortalIdRender?: string;
  coupon?: FormDataType;
  onSuccess?: VoidFunction;
}
export const CouponForm = ({
  actionPortalIdRender,
  coupon,
  onSuccess,
}: CouponCreateFormProps) => {
  const { mutate: createCoupon } = useCouponCreate({ onSuccess });
  const { mutate: updateCoupon } = useCouponUpdate({ onSuccess });
  const form = useForm<FormDataType>({
    resolver: yupResolver(YUP_SHEMA),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      ...coupon,
    },
  });

  const { data: plansResponse } = useSelfServePlans();
  const { data: agentPlansResponse } = useSelfServeAgentPlans();
  const combinedPlans = [
    ...(plansResponse?.plans ? [...plansResponse.plans].sort(planSort) : []),
    ...(agentPlansResponse?.plans
      ? [...agentPlansResponse.plans].sort(planSort)
      : []),
  ];
  const handleSubmit = ({ amount_off_dollars, ...formData }: FormDataType) => {
    // amount_off is submitted to the API in cents
    const amount_off = amount_off_dollars * 100;
    const payload = {
      ...formData,
      amount_off,
      active_on: formData.active_on?.includes('T')
        ? new Date(formData.active_on).toISOString()
        : formData.active_on,
      active_until: formData.active_until?.includes('T')
        ? new Date(formData.active_until).toISOString()
        : formData.active_until,
    };
    if (coupon) {
      // amount_off cannot be changed on update
      const { amount_off, ...restPayload } = payload;
      updateCoupon({
        ...restPayload,
      });
    } else {
      createCoupon(payload);
    }
  };
  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Flex direction="column" fullWidth>
          <FormFieldsDialogInput
            name="code"
            label="Coupon Code"
            required
            readOnly={!!coupon}
            error={form.formState.errors.code?.message}
            layoutProps={LAYOUT_PROPS}
          />
          <FormFieldsDialogInput
            name="name"
            label="Coupon Name"
            required
            error={form.formState.errors.name?.message}
            layoutProps={LAYOUT_PROPS}
          />
          <FormFieldsDialogInput
            name="amount_off_dollars"
            label="Amount Off"
            required
            onChange={(v) => {
              const val = Number(v);
              form.setValue('amount_off', val ? val * 100 : 0);
              form.clearErrors('amount_off');
            }}
            error={form.formState.errors.amount_off?.message}
            layoutProps={LAYOUT_PROPS}
            placeholder="Dollar amount of balance applied to next invoice"
            disabled={!!coupon} // amount_off cannot be changed on update
          />
          <FormFieldsDialogDropdown
            name="redemption_type"
            label="Redemption Type"
            required
            options={REDEMPTION_TYPE_DROPDOWN_OPTIONS}
            layoutProps={LAYOUT_PROPS}
          />
          <FormFieldsDialogInput
            name="redemption_cap"
            label="Redemption Cap"
            type="number"
            required
            error={form.formState.errors.redemption_cap?.message}
            layoutProps={LAYOUT_PROPS}
            placeholder="Maximum number of redemptions allowed"
          />
          <FormFieldsDialogDate
            name="active_on"
            label="Active On"
            type="datetime-local"
            error={form.formState.errors.active_on?.message}
            layoutProps={LAYOUT_PROPS_DATETIME}
          />
          <FormFieldsDialogDate
            name="active_until"
            label="Active Until"
            type="datetime-local"
            error={form.formState.errors.active_until?.message}
            layoutProps={LAYOUT_PROPS_DATETIME}
          />
          <FormFieldsDialogMultiSelect
            name="plan_ids"
            label="Self Serve Plans"
            columns={2}
            options={
              combinedPlans?.map((p) => ({
                label: getPlanLabel(p),
                value: p.id,
              })) || []
            }
            layoutProps={LAYOUT_PROPS}
          />
          <ActionButtons
            portalIdRender={actionPortalIdRender}
            dataHcName="coupon-form-actions"
            actions={[
              {
                dataHcName: 'coupon-form-submit',
                type: 'submit',
                label: coupon ? 'Update Coupon' : 'Create Coupon',
                onClick: form.handleSubmit(handleSubmit),
                disabled:
                  !form.formState.isValid || form.formState.isSubmitting,
              },
            ]}
          />
        </Flex>
      </form>
    </FormProvider>
  );
};

export const CouponFormLauncher = ({
  trigger,
  ...formProps
}: Omit<CouponCreateFormProps, 'actionPortalIdRender'> & {
  trigger: ReactNode;
}) => {
  const { active, handleOpen, handleClose } = useActiveState();
  return (
    <CapabilityCheck capabilityKey="coupon-codes-create">
      <span onClick={handleOpen}>{trigger}</span>
      <Dialog
        dataHcName="create-coupon-dialog"
        title={
          formProps.coupon
            ? `Edit Coupon: ${formProps.coupon.code}`
            : 'Create New Coupon'
        }
        active={active}
        onClose={handleClose}
      >
        <br />
        <br />
        <CouponForm
          {...formProps}
          onSuccess={() => {
            formProps.onSuccess?.();
            handleClose();
          }}
          actionPortalIdRender={DIALOG_ACTIONS_PORTAL_ID}
        />
        <br />
        <br />
      </Dialog>
    </CapabilityCheck>
  );
};
